import { createSelector } from 'reselect'
import { isMobileOnly } from 'react-device-detect'

import { RootState } from '@tabeeb/store'
import { getCurrentUserId as accountGetCurrentUserId } from '@tabeeb/modules/account/selectors'
import { getIsCurrentUserPresenter, getPresenterId } from '@tabeeb/modules/shared/content/selectors'
import { AUDIO_DEVICE, CAMERA_FACING_MODE, VIDEO_DEVICE } from '@tabeeb/modules/presentation/constants'

export const getIsCallInitializationInProgress = (state: RootState) => state.presentation.isCallInitializationInProgress
export const getIsCallLeavingInProgress = (state: RootState) => state.presentation.isCallLeavingInProgress

export const getIsCallStarted = (state: RootState) => state.presentation.connection.isCallStarted
export const getIsInPresentationMode = (state: RootState) => Boolean(state.presentation.conference.room)
export const getParticipantIdByUserId = (state: RootState, userId: number): string | undefined =>
  state.presentation.userToParticipantIdMap[userId]
export const getUserIdByParticipantId = (state: RootState, participantId: string): number | undefined =>
  state.presentation.participantToUserIdMap[participantId]

export const getCurrentUserId = (state: RootState) => accountGetCurrentUserId(state) as number
export const getLargeVideoUser = (state: RootState) => state.presentation.tracks.largeVideoUser
export const getGridViewSelectedUser = (state: RootState) => state.presentation.tracks.gridUser
export const getIsScreenSharing = (state: RootState) => state.presentation.screenSharing.isScreenSharing
export const getIsScreenSharingInProgress = (state: RootState) =>
  state.presentation.screenSharing.isScreenSharingInProgress
export const getIsPriorityViewEnabled = (state: RootState) => state.presentation.tracks.isPriorityView

export const getPreviewTracks = (state: RootState) => state.presentation.tracks.previewTracks

export const getFacingMode = (state: RootState) => state.presentation.devices.facingMode
export const getAudioInputDevice = (state: RootState) => state.presentation.devices.audioInputId
export const getAudioOutputDevice = (state: RootState) => state.presentation.devices.audioOutputId
export const getVideoInputDevice = (state: RootState) => state.presentation.devices.videoInputId

export const getIsCallPreviewOpen = (state: RootState) => state.presentation.conference.isCallPreviewOpen ?? false

export const getSelectedUser = createSelector(
  [getLargeVideoUser, getGridViewSelectedUser],
  (largeVideoUser, gridUser) => largeVideoUser || gridUser
)

export const getCurrentCallUserId = createSelector(
  [getIsCurrentUserPresenter, getSelectedUser, getCurrentUserId],
  (isPresenter, selectedUser, currentUserId) => (isPresenter ? selectedUser?.id : currentUserId)
)

export const getUserTracks = (state: RootState, { Id }: { Id: number }) => {
  return Id === getCurrentUserId(state)
    ? state.presentation.tracks.localTracks
    : state.presentation.tracks.remoteTracks[Id] ?? []
}

export const getUserTrack = (state: RootState, { Id, Type }: { Id: number; Type: string }) => {
  const userTracks = getUserTracks(state, { Id })
  if (!userTracks) {
    return undefined
  }

  return userTracks.find((track) => track.getType() === Type)
}

export const getIsUserAudioPlaying = (state: RootState, { Id }: { Id: number }) => {
  const audioTrack = getUserTrack(state, { Id, Type: AUDIO_DEVICE })
  if (!audioTrack) {
    return false
  }

  return !audioTrack.isMuted()
}

export const makeGetIsUserVideoPlaying =
  () =>
  (state: RootState, { Id }: { Id: number }) => {
    const videoTrack = getUserTrack(state, { Id, Type: VIDEO_DEVICE })
    if (!videoTrack) {
      return false
    }

    return !videoTrack.isMuted()
  }

export const getIsUserVideoPlaying = makeGetIsUserVideoPlaying()

export const getIsLargeVideoMirrored = createSelector(
  [getLargeVideoUser, getCurrentUserId, getIsScreenSharing, getIsScreenSharingInProgress, getFacingMode],
  (largeVideoUser, currentUserId, isScreenSharing, isScreenSharingInProgress, facingMode) => {
    const isScreenSharingNow = isScreenSharing && !isScreenSharingInProgress
    const isCurrentUserLarge = largeVideoUser !== null ? largeVideoUser.id === currentUserId : false

    const isUserFacingMode = facingMode === CAMERA_FACING_MODE.USER
    return !isScreenSharingNow && isCurrentUserLarge && isUserFacingMode
  }
)

export const makeGetIsUserPresenter = () =>
  createSelector([getPresenterId, (_, { userId }) => userId], (presenterId, userId) => {
    return presenterId === userId
  })

export const makeGetIsUserVideoOnMainScreen = () =>
  createSelector([getLargeVideoUser, (_, { userId }) => userId], (largeVideoUser, userId) => {
    return largeVideoUser !== null && largeVideoUser.id === userId
  })

export const makeGetIsGridUserSelected = () =>
  createSelector([getGridViewSelectedUser, (_, { userId }) => userId], (gridUser, userId) => {
    return !isMobileOnly && gridUser && gridUser.id === userId
  })

export const makeGetIsUserVideoMirrored = () =>
  createSelector(
    [getIsScreenSharing, getIsScreenSharingInProgress, getCurrentUserId, getFacingMode, (_, { userId }) => userId],
    (isScreenSharing, isScreenSharingInProgress, currentUserId, facingMode, userId) => {
      return (
        !(isScreenSharing && !isScreenSharingInProgress) &&
        userId === currentUserId &&
        facingMode === CAMERA_FACING_MODE.USER
      )
    }
  )

export const getCallStartTime = (state: RootState) => state.presentation.startCallTime
export const getIsVideoMuted = (state: RootState) => state.presentation.jitsiTrack.isVideoMuted
export const getIfCameraMutedBeforeScreenSharing = (state: RootState) =>
  state.presentation.screenSharing.cameraMutedBeforeScreenSharing

export const getIsJitsiInitialized = (state: RootState) => state.presentation.isJitsiInitialized

export const getJitsiConference = (state: RootState) => state.presentation.conference.room
export const getJitsiConnection = (state: RootState) => state.presentation.connection.connection

export const getLocalTracks = (state: RootState) => state.presentation.tracks.localTracks

export const getConferenceTracksState = (state: RootState) => state.presentation.tracks

export const getJitsiRoomId = (state: RootState) => state.presentation.connection.roomId

export const getJitsiAccessToken = (state: RootState) => state.presentation.connection.accessToken
